import * as THREE from "three";
import { createMoonTexture, createGlowTexture, createFlareTexture, createNebulaTexture, createTrailTexture } from "./textures";
import { STAR_VERTEX_SHADER, STAR_FRAGMENT_SHADER } from "./starShaders";

const LAYER_CONFIG = [
  { count: 380, depth: -55, size: 1.0, speed: 0.12 },
  { count: 240, depth: -32, size: 1.5, speed: 0.28 },
  { count: 120, depth: -14, size: 2.0, speed: 0.55 },
];

const BRIGHTNESS_TIERS = [
  { weight: 0.72, sizeMul: 0.7, alpha: 0.5, amp: 0.3 },
  { weight: 0.23, sizeMul: 1.3, alpha: 0.8, amp: 0.4 },
  { weight: 0.05, sizeMul: 2.4, alpha: 1.0, amp: 0.55 },
];

const pickTier = () => {
  const r = Math.random();
  let acc = 0;
  for (const tier of BRIGHTNESS_TIERS) {
    acc += tier.weight;
    if (r <= acc) return tier;
  }
  return BRIGHTNESS_TIERS[0];
};

const pickColor = () => {
  const r = Math.random();
  if (r < 0.7) return [1, 1, 1];
  if (r < 0.88) return [1, 0.87, 0.65];
  return [0.75, 0.85, 1];
};

export class CosmicScene {
  constructor(canvas) {
    this.canvas = canvas;
    this.scrollY = 0;
    this.isMobile = window.innerWidth < 768;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 200);
    this.camera.position.z = 12;

    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;

    this.startTime = performance.now();
    this._lastT = 0;
    this._lastScrollY = 0;
    this.scrollSpin = 0;
    this.moonState = { x: 0, y: 0.3, z: 0, scale: 2.6, lightAngle: 0 };

    this._buildStars();
    this._buildMoon();
    this._buildShootingStar();
    this._scheduleShootingStar();

    this.onResize = this.onResize.bind(this);
    window.addEventListener("resize", this.onResize);
  }

  _buildStars() {
    const countMultiplier = this.isMobile ? 0.4 : 1;
    this.starSpriteTexture = createGlowTexture(64, "255,255,255");

    this.starLayers = LAYER_CONFIG.map((cfg) => {
      const count = Math.max(30, Math.round(cfg.count * countMultiplier));
      const positions = new Float32Array(count * 3);
      const sizes = new Float32Array(count);
      const phases = new Float32Array(count);
      const twinkleSpeeds = new Float32Array(count);
      const twinkleAmps = new Float32Array(count);
      const colors = new Float32Array(count * 3);

      for (let i = 0; i < count; i++) {
        positions[i * 3] = (Math.random() - 0.5) * 70;
        positions[i * 3 + 1] = (Math.random() - 0.5) * 70;
        positions[i * 3 + 2] = cfg.depth + (Math.random() - 0.5) * 6;

        const tier = pickTier();
        sizes[i] = cfg.size * tier.sizeMul * (0.75 + Math.random() * 0.5);
        phases[i] = Math.random() * Math.PI * 2;
        twinkleSpeeds[i] = 0.5 + Math.random() * 1.3;
        twinkleAmps[i] = tier.amp * (0.7 + Math.random() * 0.6);

        const [r, g, b] = pickColor();
        colors[i * 3] = r * tier.alpha;
        colors[i * 3 + 1] = g * tier.alpha;
        colors[i * 3 + 2] = b * tier.alpha;
      }

      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("size", new THREE.BufferAttribute(sizes, 1));
      geometry.setAttribute("phase", new THREE.BufferAttribute(phases, 1));
      geometry.setAttribute("twinkleSpeed", new THREE.BufferAttribute(twinkleSpeeds, 1));
      geometry.setAttribute("twinkleAmp", new THREE.BufferAttribute(twinkleAmps, 1));
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

      const material = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 0 },
          pixelRatio: { value: Math.min(window.devicePixelRatio, 2) },
          map: { value: this.starSpriteTexture },
        },
        vertexShader: STAR_VERTEX_SHADER,
        fragmentShader: STAR_FRAGMENT_SHADER,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      });

      const points = new THREE.Points(geometry, material);
      points.userData.speed = cfg.speed;
      this.scene.add(points);
      return points;
    });

    this._buildFlareStars();
    this._buildNebula();
  }

  _buildFlareStars() {
    const count = this.isMobile ? 4 : 9;
    const texture = createFlareTexture();
    this.flareStars = [];
    for (let i = 0; i < count; i++) {
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        opacity: 0.5,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const sprite = new THREE.Sprite(material);
      const scale = 0.9 + Math.random() * 0.7;
      sprite.scale.set(scale, scale, 1);
      sprite.position.set((Math.random() - 0.5) * 50, (Math.random() - 0.5) * 40, -18 + Math.random() * 10);
      sprite.userData.phase = Math.random() * Math.PI * 2;
      this.scene.add(sprite);
      this.flareStars.push(sprite);
    }
  }

  _buildNebula() {
    const texture = createNebulaTexture();
    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      opacity: 0.13,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });
    this.nebula = new THREE.Sprite(material);
    this.nebula.scale.set(85, 85, 1);
    this.nebula.position.set(-6, 3, -70);
    this.scene.add(this.nebula);
  }

  _buildMoon() {
    const segments = this.isMobile ? 28 : 64;
    this.moonGroup = new THREE.Group();
    this.scene.add(this.moonGroup);

    const geometry = new THREE.SphereGeometry(1, segments, segments);
    const material = new THREE.MeshStandardMaterial({
      map: createMoonTexture(this.isMobile ? 256 : 512),
      roughness: 0.92,
      metalness: 0,
    });
    this.moon = new THREE.Mesh(geometry, material);
    this.moon.rotation.z = 0.22;
    this.moonGroup.add(this.moon);

    const glowMaterial = new THREE.SpriteMaterial({
      map: createGlowTexture(),
      color: 0xe8c27a,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.glow = new THREE.Sprite(glowMaterial);
    this.glow.scale.set(1.9, 1.9, 1);
    this.moonGroup.add(this.glow);

    const ringGeometry = new THREE.RingGeometry(1.2, 1.23, 80);
    const ringMaterial = new THREE.MeshBasicMaterial({
      color: 0xe8c27a,
      transparent: true,
      opacity: 0.16,
      side: THREE.DoubleSide,
      depthWrite: false,
    });
    this.orbitRing = new THREE.Mesh(ringGeometry, ringMaterial);
    this.orbitRing.rotation.x = Math.PI / 2.3;
    this.orbitRing.rotation.z = 0.15;
    this.moonGroup.add(this.orbitRing);

    this.orbitPivots = [];
    const orbiterConfigs = [
      { radius: 1.27, speed: 0.6, color: 0xf6e3b4, size: 0.032 },
      { radius: 1.33, speed: -0.4, color: 0xa99bff, size: 0.026 },
    ];
    orbiterConfigs.forEach((cfg) => {
      const pivot = new THREE.Object3D();
      pivot.rotation.x = this.orbitRing.rotation.x;
      pivot.rotation.z = this.orbitRing.rotation.z;
      pivot.rotation.y = Math.random() * Math.PI * 2;
      this.moonGroup.add(pivot);

      const orbMaterial = new THREE.MeshBasicMaterial({ color: cfg.color, transparent: true, opacity: 0.9 });
      const orb = new THREE.Mesh(new THREE.SphereGeometry(cfg.size, 10, 10), orbMaterial);
      orb.position.set(cfg.radius, 0, 0);
      pivot.add(orb);
      this.orbitPivots.push({ pivot, speed: cfg.speed });
    });

    this.moonLight = new THREE.DirectionalLight(0xfff3da, 1.5);
    this.scene.add(this.moonLight);
    this.scene.add(new THREE.AmbientLight(0x1a1530, 0.55));
  }

  _buildShootingStar() {
    const geometry = new THREE.PlaneGeometry(2.6, 0.06);
    const material = new THREE.MeshBasicMaterial({
      map: createTrailTexture(),
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    this.shootingStar = new THREE.Mesh(geometry, material);
    this.shootingStar.position.z = -20;
    this.scene.add(this.shootingStar);
  }

  _scheduleShootingStar() {
    const delay = 9000 + Math.random() * 7000;
    this._shootTimer = setTimeout(() => {
      this._fireShootingStar();
      this._scheduleShootingStar();
    }, delay);
  }

  _fireShootingStar() {
    if (!window.gsap) return;
    const startX = -20 + Math.random() * 10;
    const startY = 8 + Math.random() * 6;
    this.shootingStar.position.set(startX, startY, -20);
    this.shootingStar.rotation.z = -0.4 - Math.random() * 0.3;
    this.shootingStar.material.opacity = 0;

    window.gsap
      .timeline()
      .to(this.shootingStar.material, { opacity: 0.85, duration: 0.2 })
      .to(this.shootingStar.position, { x: startX + 14, y: startY - 7, duration: 0.9, ease: "power1.in" }, 0)
      .to(this.shootingStar.material, { opacity: 0, duration: 0.45 }, 0.5);
  }

  onResize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }

  setScrollOffset(y) {
    this.scrollY = y;
  }

  render() {
    const t = (performance.now() - this.startTime) / 1000;
    const dt = Math.min(0.05, Math.max(0, t - this._lastT));
    this._lastT = t;

    this.starLayers.forEach((layer) => {
      layer.material.uniforms.time.value = t;
      layer.position.y = this.scrollY * 0.0025 * layer.userData.speed * 10;
    });

    this.flareStars.forEach((sprite) => {
      sprite.material.opacity = 0.3 + 0.28 * Math.sin(t * 0.8 + sprite.userData.phase);
    });

    if (this.nebula) {
      this.nebula.material.rotation += dt * 0.006;
    }

    const scrollDelta = Math.abs(this.scrollY - this._lastScrollY);
    this._lastScrollY = this.scrollY;
    this.scrollSpin += (scrollDelta * 0.0006 - this.scrollSpin) * 0.08;

    const s = this.moonState;
    const floatY = Math.sin(t * 0.35) * 0.09;
    this.moonGroup.position.set(s.x, s.y + floatY, s.z);
    this.moonGroup.scale.setScalar(s.scale);

    this.moon.rotation.y += dt * (0.09 + this.scrollSpin);

    this.orbitPivots.forEach(({ pivot, speed }) => {
      pivot.rotation.y += dt * speed;
    });

    const brightness = s.brightness ?? 1;
    this.moonLight.intensity = 1.5 * brightness;
    this.glow.material.opacity = 0.8 * brightness;

    const R = 6;
    this.moonLight.position.set(
      this.moonGroup.position.x + Math.sin(s.lightAngle) * R,
      this.moonGroup.position.y + 2.0,
      this.moonGroup.position.z + Math.cos(s.lightAngle) * R
    );

    this.renderer.render(this.scene, this.camera);
  }

  dispose() {
    clearTimeout(this._shootTimer);
    window.removeEventListener("resize", this.onResize);
    this.starLayers.forEach((layer) => {
      layer.geometry.dispose();
      layer.material.dispose();
    });
    this.starSpriteTexture.dispose();
    this.flareStars.forEach((sprite) => {
      sprite.material.map.dispose();
      sprite.material.dispose();
    });
    if (this.nebula) {
      this.nebula.material.map.dispose();
      this.nebula.material.dispose();
    }
    this.moon.geometry.dispose();
    this.moon.material.dispose();
    this.orbitRing.geometry.dispose();
    this.orbitRing.material.dispose();
    this.renderer.dispose();
  }
}
